import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { ClipboardCheck, Loader2, Download, RotateCw, Eye, X } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-IE", { timeZone: "Europe/Dublin" });

export default function Reconciliation() {
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = () => api.get("/admin/reconciliations").then(({ data }) => setReports(data));
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setBusy(true);
    try { await api.post("/admin/reconciliations/run"); toast.success("Reconciliation complete"); await load(); }
    catch { toast.error("Reconciliation failed"); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl font-bold text-slate-50">Sage Reconciliation</h1>
          <p className="mt-1 text-sm text-[#94A3B8]">Nightly safety check at 02:00 Europe/Dublin. Run on demand below.</p>
        </div>
        <button data-testid="recon-run-btn" disabled={busy} onClick={runNow} className="mc-btn-gold inline-flex items-center gap-2 rounded-full px-6 py-3 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Run Full Reconciliation
        </button>
      </div>

      <div className="mt-8 space-y-4">
        {reports.length === 0 && <div className="rounded-2xl border border-[#2A2E39] p-12 text-center text-[#64748B]">No reconciliation reports yet.</div>}
        {reports.map((r) => (
          <div key={r.id} data-testid={`recon-report-${r.id}`} className="rounded-2xl border border-[#2A2E39] bg-[#1A1D24] p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#D4AF37]">Sage Reconciliation</div>
                <div className="font-mono-data text-lg font-bold text-slate-100">{fmt(r.run_at)}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`recon-view-${r.id}`} onClick={() => setViewing(r)} className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2E39] px-4 py-2 text-sm text-slate-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"><Eye className="h-4 w-4" /> View Issues</button>
                <a data-testid={`recon-export-${r.id}`} href={`${API}/admin/reconciliations/${r.id}/export`} className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2E39] px-4 py-2 text-sm text-slate-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"><Download className="h-4 w-4" /> Export CSV</a>
                <button data-testid={`recon-rerun-${r.id}`} onClick={runNow} className="inline-flex items-center gap-1.5 rounded-full border border-[#2A2E39] px-4 py-2 text-sm text-slate-200 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"><RotateCw className="h-4 w-4" /> Re-run</button>
              </div>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-3 lg:grid-cols-6 text-sm">
              {[["Products checked", r.products_checked], ["Stock records", r.stock_records_checked], ["Matched", r.matched], ["Discrepancies", r.discrepancies], ["Orders checked", r.orders_checked], ["Unmapped customers", r.unmapped_customers]].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-3">
                  <div className="text-xs text-[#64748B]">{k}</div>
                  <div className="mt-0.5 text-xl font-bold text-slate-100">{v}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setViewing(null)}>
          <div className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-2xl border border-[#2A2E39] bg-[#121418] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl font-bold text-slate-50">Reconciliation Issues</h3>
              <button data-testid="recon-modal-close" onClick={() => setViewing(null)} className="rounded-lg p-2 text-slate-400 hover:text-rose-400"><X className="h-5 w-5" /></button>
            </div>
            {viewing.issues.length === 0 ? (
              <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center text-emerald-300">No issues — everything reconciled cleanly. ✓</div>
            ) : (
              <div className="mt-4 space-y-2">
                {viewing.issues.map((issue, i) => (
                  <div key={`${issue.type}-${issue.sku}-${i}`} className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-3">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full border border-amber-500/40 px-2 py-0.5 text-[10px] font-bold text-amber-400">{issue.type}</span>
                      <span className="font-mono-data text-sm text-[#D4AF37]">{issue.sku}</span>
                    </div>
                    <div className="mt-1 text-sm text-[#94A3B8]">{issue.detail}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
